import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { money, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button as UIButton } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Check, AlertTriangle, Info, Smartphone, MessageCircle, Loader2, Star, ArrowLeft, Sparkles } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (p) => (!p ? "" : p.startsWith("http") ? p : `${BACKEND}${p}`);
const GOLD = { background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" };

const BENEFITS = [
  "Aprovação mediante análise de crédito, inclusive com possibilidade para negativados",
  "Parcelamento em boletos mensais ou quinzenais, conforme a financeira aprovada",
  "Sem necessidade de cartão de crédito",
  "Aparelhos novos e lacrados",
  "Atendimento rápido e simulação sem compromisso",
];

const deviceName = (d) => [d.brand, d.model, d.storage].filter(Boolean).join(" ");

export default function BoletoCelular() {
  const [data, setData] = useState(null);
  const [brand, setBrand] = useState("");
  const [lead, setLead] = useState(null);

  useEffect(() => { api.get("/public/boleto").then((r) => setData(r.data)).catch(() => setData({ devices: [] })); }, []);

  const allDevices = data?.devices || [];
  const brands = [...new Set(allDevices.map((d) => d.brand).filter(Boolean))].sort();
  const devices = brand ? allDevices.filter((d) => d.brand === brand) : allDevices;
  const hasAndroid = devices.some((d) => d.finance?.rule === "android");
  const wa = (data?.company?.whatsapp || "").replace(/\D/g, "");

  const openWa = (d, msg) => setLead({ d, msg });

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white">
      <header className="alpha-glass sticky top-0 z-30 h-16 flex items-center px-4 lg:px-8">
        <Link to="/" data-testid="boleto-back-home" className="inline-flex items-center gap-2 text-zinc-300 hover:text-white text-sm"><ArrowLeft size={16} />Voltar ao início</Link>
      </header>

      <main className="max-w-6xl mx-auto px-4 lg:px-8 py-10" data-testid="boleto-page">
        <div className="inline-flex items-center gap-2 rounded-full bg-gold/10 border border-gold/30 text-gold-bright px-4 py-1.5 text-sm mb-5">
          <Smartphone size={15} /> Celular no boleto
        </div>
        <h1 className="text-4xl sm:text-5xl font-display font-extrabold tracking-tight">
          Celular no boleto é na <span className="alpha-gold-text">Alpha Prime Cell</span>
        </h1>
        <p className="text-zinc-300 mt-4 leading-relaxed max-w-3xl">
          Trabalhamos com venda de celulares parcelados no boleto através de financeiras parceiras.
          Escolha o aparelho abaixo e faça sua simulação <strong>sem compromisso</strong>.
        </p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-8">
          {BENEFITS.map((b) => (
            <div key={b} className="alpha-card p-4 flex items-start gap-3">
              <span className="h-6 w-6 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0 mt-0.5"><Check size={14} className="text-emerald-400" /></span>
              <span className="text-sm text-zinc-300">{b}</span>
            </div>
          ))}
        </div>

        <div className="alpha-card p-4 flex items-start gap-3 mt-3">
          <Info size={18} className="text-gold-bright shrink-0 mt-0.5" />
          <p className="text-sm text-zinc-300">Aparelhos <strong>Android</strong> têm aprovação mais simples. Para <strong>iPhone</strong>, a análise é mais criteriosa.</p>
        </div>

        <div className="mt-12">
          <h2 className="text-lg font-display font-bold text-white">Aparelhos disponíveis</h2>
          {brands.length > 1 && <BrandFilter brands={brands} brand={brand} setBrand={setBrand} />}
          {hasAndroid && <AndroidNotices />}
          {!data ? (
            <div className="flex justify-center py-16"><Loader2 className="animate-spin text-gold" /></div>
          ) : devices.length === 0 ? (
            <div className="alpha-card p-12 text-center mt-4" data-testid="boleto-empty">
              <Smartphone className="mx-auto text-zinc-600" size={40} />
              <p className="text-zinc-400 mt-3">Em breve novos aparelhos. Fale conosco no WhatsApp para consultar disponibilidade.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-5" data-testid="boleto-grid">
              {devices.map((d) => <DeviceCard key={d.id} d={d} onWa={openWa} />)}
            </div>
          )}
        </div>
      </main>
      {lead && <LeadDialog device={lead.d} intro={lead.msg} wa={wa} onClose={() => setLead(null)} />}
    </div>
  );
}

const L = ({ t, children }) => (<div><Label className="text-xs text-zinc-400 mb-1.5 block">{t}</Label>{children}</div>);

function LeadDialog({ device, intro, wa, onClose }) {
  const [f, setF] = useState({ fullName: "", cpf: "", phone: "", email: "", cep: "", message: "" });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const name = deviceName(device);

  const submit = async () => {
    if (!f.fullName.trim() || !f.phone.trim()) { toast.error("Preencha nome completo e celular."); return; }
    setSaving(true);
    try {
      await api.post("/public/boleto/lead", { ...f, deviceId: device.id, deviceName: name });
      setDone(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const waMsg = [intro, `Meu nome é ${f.fullName.trim()}.`, f.cpf && `CPF: ${f.cpf}`, `Celular: ${f.phone}`,
    f.email && `E-mail: ${f.email}`, f.cep && `CEP: ${f.cep}`, f.message && `Mensagem: ${f.message}`].filter(Boolean).join("\n");
  const waLink = `https://wa.me/${wa}?text=${encodeURIComponent(waMsg)}`;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121217] border-gold/20 text-white max-w-md" data-testid="boleto-lead-dialog">
        <DialogHeader><DialogTitle className="font-display">Consultar {name}</DialogTitle></DialogHeader>
        {done ? (
          <div className="text-center py-4" data-testid="boleto-lead-success">
            <span className="h-14 w-14 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto"><Check size={28} className="text-emerald-400" /></span>
            <p className="text-white font-semibold mt-4">Recebemos seus dados!</p>
            <p className="text-sm text-zinc-400 mt-1">Nossa equipe vai entrar em contato. Para agilizar, envie seus dados agora pelo WhatsApp.</p>
            {wa && <a href={waLink} target="_blank" rel="noreferrer" data-testid="boleto-lead-wa" className="inline-flex items-center justify-center gap-2 mt-5 bg-green-600 hover:bg-green-500 text-white px-5 py-2.5 rounded-full text-sm font-semibold"><MessageCircle size={16} /> Enviar pelo WhatsApp</a>}
          </div>
        ) : (
          <>
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
              <L t="Nome completo *"><Input data-testid="lead-name" value={f.fullName} onChange={set("fullName")} className="i" /></L>
              <L t="CPF"><Input data-testid="lead-cpf" value={f.cpf} onChange={set("cpf")} className="i" placeholder="000.000.000-00" /></L>
              <L t="Celular / WhatsApp *"><Input data-testid="lead-phone" value={f.phone} onChange={set("phone")} className="i" placeholder="(61) 90000-0000" /></L>
              <L t="E-mail"><Input data-testid="lead-email" value={f.email} onChange={set("email")} className="i" /></L>
              <L t="CEP"><Input data-testid="lead-cep" value={f.cep} onChange={set("cep")} className="i" placeholder="70000-000" /></L>
              <L t="Mensagem (opcional)"><Textarea data-testid="lead-msg" value={f.message} onChange={set("message")} className="i" rows={2} /></L>
            </div>
            <DialogFooter>
              <UIButton data-testid="lead-cancel" variant="ghost" onClick={onClose} className="text-zinc-400">Cancelar</UIButton>
              <UIButton data-testid="lead-submit" onClick={submit} disabled={saving} className="text-black font-semibold" style={GOLD}>
                {saving ? <Loader2 size={16} className="animate-spin" /> : "Enviar dados"}
              </UIButton>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BrandFilter({ brands, brand, setBrand }) {
  const cls = (on) => `px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${on ? "bg-gold/15 border-gold/40 text-gold-bright" : "border-white/10 text-zinc-400 hover:text-white hover:border-white/20"}`;
  return (
    <div className="flex flex-wrap gap-2 mt-4" data-testid="boleto-brand-filters">
      <button data-testid="boleto-brand-all" onClick={() => setBrand("")} className={cls(brand === "")}>Todas</button>
      {brands.map((b) => <button key={b} data-testid={`boleto-brand-${b}`} onClick={() => setBrand(b)} className={cls(brand === b)}>{b}</button>)}
    </div>
  );
}

function AndroidNotices() {
  return (
    <div className="grid lg:grid-cols-2 gap-3 mt-5">
      <div className="alpha-card p-5 border-emerald-500/30 bg-emerald-500/[0.04]" data-testid="boleto-android-entry-notice">
        <p className="font-display font-bold text-emerald-300 flex items-center gap-2"><Sparkles size={16} />A entrada não é obrigatória em todos os casos!</p>
        <p className="text-sm text-zinc-300 mt-2 leading-relaxed">Os valores apresentados são estimativas para facilitar sua simulação. Dependendo do seu perfil de crédito e da análise da financeira parceira, você poderá conseguir condições diferenciadas, incluindo entrada reduzida ou até mesmo financiamento sem entrada.</p>
        <p className="text-sm text-zinc-300 mt-2 leading-relaxed">Consulte nossa equipe para verificar as condições disponíveis para você. A aprovação, a entrada e os valores das parcelas dependem da análise da financeira.</p>
      </div>
      <div className="alpha-card p-5 border-amber-500/30 flex items-start gap-3" data-testid="boleto-estimate-notice">
        <AlertTriangle size={18} className="text-amber-400 shrink-0 mt-0.5" />
        <p className="text-sm text-zinc-300 leading-relaxed">“Os valores de entrada e das parcelas são estimativas para facilitar sua simulação. As condições definitivas, incluindo entrada, valores das parcelas e aprovação do crédito, dependem da análise e das regras da financeira parceira.”</p>
      </div>
    </div>
  );
}

function DeviceCard({ d, onWa }) {
  const special = d.finance?.rule === "special";
  return (
    <div data-testid={`boleto-card-${d.id}`} className="alpha-card overflow-hidden group flex flex-col">
      <div className="aspect-square overflow-hidden bg-[#0A0A0C] relative">
        {d.photos?.[0] ? (
          <img src={imgUrl(d.photos[0])} alt={deviceName(d)} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-700"><Smartphone size={44} /></div>
        )}
        {d.featured && <span className="absolute top-2 right-2 text-[10px] px-2 py-0.5 rounded-full bg-gold/90 text-black font-semibold flex items-center gap-1"><Star size={10} className="fill-black" />Destaque</span>}
        {d.condition && <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-full bg-black/70 text-zinc-200">{d.condition}</span>}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-semibold text-white leading-tight">{d.brand} {d.model}</h3>
        <p className="text-sm text-zinc-500 mt-1">{[d.storage, d.color].filter(Boolean).join(" · ")}</p>
        {(d.tag_easy || d.tag_credit) && (
          <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`boleto-tags-${d.id}`}>
            {d.tag_easy && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-medium">✓ Fácil aprovação</span>}
            {d.tag_credit && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 font-medium">★ Para cliente com excelente perfil de crédito</span>}
          </div>
        )}
        {special ? <IphoneBlock d={d} onWa={onWa} /> : d.finance?.rule === "credit" ? <CreditBlock d={d} onWa={onWa} /> : <AndroidBlock d={d} onWa={onWa} />}
      </div>
    </div>
  );
}

function AndroidBlock({ d, onWa }) {
  const f = d.finance || {};
  const parc = (f.installments || []).map((i) => `${i.n}x de ${money(i.value)}`).join(", ");
  const msg = `Olá! Quero consultar as condições do ${deviceName(d)} no boleto. Vi a simulação: entrada estimada de ${money(f.entry)} e parcelas estimadas de ${parc}.`;
  return (
    <div className="flex flex-col flex-1" data-testid={`boleto-android-${d.id}`}>
      <div className="mt-3 rounded-lg bg-gold/[0.06] border border-gold/20 p-3">
        <p className="text-[11px] uppercase tracking-wide text-zinc-400">Entrada estimada</p>
        <p className="text-xl font-mono-kpi font-extrabold text-gold-bright" data-testid={`boleto-entry-${d.id}`}>{money(f.entry)}</p>
        <div className="mt-2 space-y-1">
          {(f.installments || []).map((i) => (
            <div key={i.n} className="flex justify-between text-sm" data-testid={`boleto-inst-${d.id}-${i.n}`}>
              <span className="text-zinc-400">{i.n}x de</span>
              <span className="font-mono-kpi font-semibold text-white">{money(i.value)}</span>
            </div>
          ))}
        </div>
      </div>
      <p className="text-[11px] text-zinc-500 mt-2">Valores estimados · O valor da entrada é definido após análise do CPF pela financeira.</p>
      <Button onClick={onWa && (() => onWa(d, msg))} testid={`boleto-wa-${d.id}`}>Consultar condições pelo WhatsApp</Button>
    </div>
  );
}

function IphoneBlock({ d, onWa }) {
  const msg = `Olá! Quero consultar o parcelamento sem entrada do ${deviceName(d)}.`;
  return (
    <div className="flex flex-col flex-1" data-testid={`boleto-iphone-${d.id}`}>
      <span className="text-xl font-mono-kpi font-extrabold text-gold-bright mt-3" data-testid={`boleto-price-${d.id}`}>{money(d.price)}</span>
      <div className="mt-3 rounded-lg bg-white/[0.03] border border-gold/20 p-3">
        <p className="font-display font-extrabold text-sm text-gold-bright">PARCELE SEU IPHONE SEM ENTRADA!</p>
        <p className="text-xs text-zinc-300 mt-1.5 leading-relaxed">Para clientes com excelente perfil de crédito, existe a possibilidade de parcelamento sem entrada.</p>
        <p className="text-[11px] text-zinc-500 mt-1.5 leading-relaxed">Consulte nossas condições pelo WhatsApp. A aprovação, a disponibilidade de parcelamento sem entrada e os valores das parcelas dependem da análise de crédito e das condições oferecidas pela financeira parceira.</p>
      </div>
      {d.finance?.upTo24 && <Upto24 id={d.id} />}
      <Button onClick={onWa && (() => onWa(d, msg))} testid={`boleto-wa-${d.id}`}>Consultar parcelamento sem entrada</Button>
    </div>
  );
}

const Upto24 = ({ id }) => (
  <p className="text-sm text-emerald-300 font-semibold mt-3" data-testid={`boleto-24x-${id}`}>Parcelamento em até 24x, a depender da financeira.</p>
);

function CreditBlock({ d, onWa }) {
  const msg = `Olá! Quero consultar as condições de parcelamento do ${deviceName(d)}.`;
  return (
    <div className="flex flex-col flex-1" data-testid={`boleto-credit-${d.id}`}>
      <span className="text-xl font-mono-kpi font-extrabold text-gold-bright mt-3" data-testid={`boleto-price-${d.id}`}>{money(d.price)}</span>
      <Upto24 id={d.id} />
      <p className="text-[11px] text-zinc-500 mt-1.5 leading-relaxed">Aprovação, prazo e valores das parcelas dependem da análise de crédito da financeira parceira.</p>
      <Button onClick={onWa && (() => onWa(d, msg))} testid={`boleto-wa-${d.id}`}>Consultar condições pelo WhatsApp</Button>
    </div>
  );
}

const Button = ({ onClick, testid, children }) => (
  <><div className="flex-1" />{onClick && <button data-testid={testid} onClick={onClick} style={GOLD}
    className="w-full text-black font-semibold min-h-9 py-2 px-3 rounded-md text-sm inline-flex items-center justify-center gap-2 hover:brightness-110 transition-[filter] mt-4">
    <MessageCircle size={15} className="shrink-0" />{children}
  </button>}</>
);
